import React, { useState } from 'react';
import { Menu, useSidebarState, usePermissions } from 'react-admin';
import {
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BookIcon from '@mui/icons-material/Book';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PeopleIcon from '@mui/icons-material/People';
import MailIcon from '@mui/icons-material/Mail';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useLocation } from 'react-router-dom';
import { useT } from './i18n/useT';

// Jumpstart Spine-style tokens: a dark charcoal navigation rail matching the
// @wk Spine component. Light gray text on a dark surface, blue selected state
// and a charcoal hover, mirroring the real Spine styling.
const SPINE_BG = 'var(--cg3-color-gray-750, #232323)';
const SPINE_TEXT = 'var(--cg3-color-gray-100, #dadada)';
const SPINE_TEXT_ON = 'var(--cg3-color-monochrome-white, #ffffff)';
const SPINE_HOVER_BG = 'var(--cg3-color-gray-500, #474747)';
const SPINE_SELECTED_BG = 'var(--cg3-color-blue-500, #007ac3)';
const SPINE_ITEM_MIN_H = 40;
const SPINE_SUBITEM_MIN_H = 30;

const ProcessingMenuGroup = () => {
    const [open, setOpen] = useState(true);
    const [sidebarOpen] = useSidebarState();
    const location = useLocation();
    const t = useT();

    const isActive =
        location.pathname.startsWith('/pdf-watermark') ||
        location.pathname.startsWith('/epub-sign');

    const groupButton = (
        <ListItemButton
            onClick={() => setOpen(o => !o)}
            sx={{
                flexGrow: 0,
                minHeight: SPINE_ITEM_MIN_H,
                px: 'var(--cg3-spacing, 16px)',
                color: isActive ? SPINE_TEXT_ON : SPINE_TEXT,
                backgroundColor: isActive ? SPINE_SELECTED_BG : 'transparent',
                transition: 'background-color 150ms ease, color 150ms ease',
                '&:hover': {
                    backgroundColor: isActive ? SPINE_SELECTED_BG : SPINE_HOVER_BG,
                    color: SPINE_TEXT_ON,
                },
            }}
        >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <PictureAsPdfIcon />
            </ListItemIcon>
            {sidebarOpen && (
                <>
                    <ListItemText
                        primary={t('menu.processing')}
                    />
                    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </>
            )}
        </ListItemButton>
    );

    return (
        <>
            {sidebarOpen ? groupButton : (
                <Tooltip title={t('menu.processing')} placement="right">
                    {groupButton}
                </Tooltip>
            )}
            <Collapse in={open && sidebarOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding className="faust-submenu">
                    <Menu.Item
                        to="/pdf-watermark"
                        primaryText={t('menu.pdfWatermarkDb')}
                        leftIcon={<PictureAsPdfIcon />}
                    />
                    <Menu.Item
                        to="/epub-sign"
                        primaryText={t('menu.epubSignDb')}
                        leftIcon={<BookIcon />}
                    />
                </List>
            </Collapse>
        </>
    );
};

const AppManagementMenuGroup = () => {
    const [open, setOpen] = useState(true);
    const [sidebarOpen] = useSidebarState();
    const location = useLocation();
    const t = useT();

    const isActive =
        location.pathname.startsWith('/catalog-sync') ||
        location.pathname.startsWith('/mass-upload') ||
        location.pathname.startsWith('/verify') ||
        location.pathname.startsWith('/fingerprint-verify');

    const groupButton = (
        <ListItemButton
            onClick={() => setOpen(o => !o)}
            sx={{
                flexGrow: 0,
                minHeight: SPINE_ITEM_MIN_H,
                px: 'var(--cg3-spacing, 16px)',
                color: isActive ? SPINE_TEXT_ON : SPINE_TEXT,
                backgroundColor: isActive ? SPINE_SELECTED_BG : 'transparent',
                transition: 'background-color 150ms ease, color 150ms ease',
                '&:hover': {
                    backgroundColor: isActive ? SPINE_SELECTED_BG : SPINE_HOVER_BG,
                    color: SPINE_TEXT_ON,
                },
            }}
        >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <SettingsIcon />
            </ListItemIcon>
            {sidebarOpen && (
                <>
                    <ListItemText
                        primary={t('menu.applicationManagement')}
                    />
                    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </>
            )}
        </ListItemButton>
    );

    return (
        <>
            {sidebarOpen ? groupButton : (
                <Tooltip title={t('menu.applicationManagement')} placement="right">
                    {groupButton}
                </Tooltip>
            )}
            <Collapse in={open && sidebarOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding className="faust-submenu">
                    <Menu.Item
                        to="/catalog-sync"
                        primaryText={t('menu.catalogSync')}
                        leftIcon={<StorageIcon />}
                    />
                    <Menu.Item
                        to="/mass-upload"
                        primaryText={t('menu.massUpload')}
                        leftIcon={<CloudUploadIcon />}
                    />
                    <Menu.Item
                        to="/verify"
                        primaryText={t('menu.verifyPipeline')}
                        leftIcon={<VerifiedUserIcon />}
                    />
                    <Menu.Item
                        to="/fingerprint-verify"
                        primaryText={t('menu.fingerprintCheck')}
                        leftIcon={<FingerprintIcon />}
                    />
                </List>
            </Collapse>
        </>
    );
};

export const CustomMenu = () => {
    const { permissions } = usePermissions();
    const t = useT();

    return (
        <Menu
            sx={{
                backgroundColor: SPINE_BG,
                minHeight: '100%',
                py: 0,
                // react-admin's MenuItemLink renders an MUI MenuItem but does NOT
                // emit a standalone `.RaMenuItemLink-root` class (only an Emotion
                // hashed class), so base styling must target `.MuiMenuItem-root`.
                '& .MuiMenuItem-root': {
                    minHeight: SPINE_ITEM_MIN_H,
                    paddingLeft: 'var(--cg3-spacing, 16px)',
                    paddingRight: 'var(--cg3-spacing, 16px)',
                    borderRadius: 0,
                    color: SPINE_TEXT,
                    fontSize: 'var(--cg3-font-size-base, 0.875rem)',
                    transition: 'background-color 150ms ease, color 150ms ease',
                    '&:hover': {
                        backgroundColor: SPINE_HOVER_BG,
                        color: SPINE_TEXT_ON,
                    },
                },
                '& .MuiListItemIcon-root': {
                    color: 'inherit',
                    minWidth: 40,
                },
                // Compact, indented sub-items inside the collapsible groups.
                '& .faust-submenu .MuiMenuItem-root': {
                    minHeight: SPINE_SUBITEM_MIN_H,
                    paddingTop: 'var(--cg3-spacing-quarter, 4px)',
                    paddingBottom: 'var(--cg3-spacing-quarter, 4px)',
                    paddingLeft: 'var(--cg3-spacing-double, 32px)',
                    fontSize: 'var(--cg3-font-size-base, 0.875rem)',
                },
                '& .faust-submenu .MuiListItemIcon-root': {
                    minWidth: 32,
                    '& svg': { fontSize: '1.125rem' },
                },
                '& .MuiMenuItem-root.RaMenuItemLink-active': {
                    backgroundColor: SPINE_SELECTED_BG,
                    color: SPINE_TEXT_ON,
                    fontWeight: 'var(--cg3-font-weight-medium, 600)',
                    '&:hover': {
                        backgroundColor: SPINE_SELECTED_BG,
                    },
                    '& .MuiListItemIcon-root': {
                        color: SPINE_TEXT_ON,
                    },
                },
                // Group header buttons (Zpracování, Správa aplikace) — match item font size
                '& .MuiListItemButton-root .MuiListItemText-primary': {
                    fontSize: 'var(--cg3-font-size-base, 0.875rem)',
                },
            }}
        >
            <Menu.Item
                to="/"
                primaryText={t('menu.overview')}
                leftIcon={<DashboardIcon />}
            />
            <Menu.Item
                to="/publications"
                primaryText={t('menu.publications')}
                leftIcon={<DescriptionIcon />}
            />
            <Menu.Item
                to="/files"
                primaryText={t('menu.files')}
                leftIcon={<AttachFileIcon />}
            />
            <ProcessingMenuGroup />
            <Menu.Item
                to="/queue"
                primaryText={t('menu.monitoring')}
                leftIcon={<MonitorHeartIcon />}
            />
            <Menu.Item
                to="/users"
                primaryText={t('menu.users')}
                leftIcon={<PeopleIcon />}
            />
            {permissions === 'admin' && (
                <Menu.Item
                    to="/invitations"
                    primaryText={t('menu.invitations')}
                    leftIcon={<MailIcon />}
                />
            )}
            {permissions === 'admin' && (
                <Menu.Item
                    to="/oauth-applications"
                    primaryText={t('menu.oauthApplications')}
                    leftIcon={<VpnKeyIcon />}
                />
            )}
            {permissions === 'admin' && <AppManagementMenuGroup />}
        </Menu>
    );
};
